import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { wallTime, wallTimeCandidates } from '../lib/scheduled-meetings.js';

/** Round the instant first so defaults remain valid across midnight and DST changes. */
export function nextMeetingTimes(zone: string, now = Date.now()) {
  const start = Math.floor(now / 900_000) * 900_000 + 900_000;
  return { start: wallTime(new Date(start).toISOString(), zone), end: wallTime(new Date(start + 3_600_000).toISOString(), zone) };
}

export function MeetingDateTimePicker({ label, value, onChange, disabled = false, timezone, preservedValue }: {
  label: string; value: string; onChange: (value: string) => void; disabled?: boolean;
  timezone: string; preservedValue?: string;
}) {
  const id = useId();
  const timeRef = useRef<HTMLInputElement>(null);
  const [now, setNow] = useState(Date.now);
  const [date = '', time = ''] = value.split('T');
  let minimum = '';
  try { minimum = wallTime(new Date(now).toISOString(), timezone); } catch { /* The form reports invalid zones. */ }
  const preserved = value === preservedValue;
  const candidates = useMemo(() => wallTimeCandidates(value, timezone), [value, timezone]);
  const past = !preserved && candidates.length > 0 && candidates.every(candidate => Date.parse(candidate) <= now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    timeRef.current?.setCustomValidity(past ? 'Choose a future meeting time.' : '');
  }, [past]);
  return <fieldset className="meeting-datetime" disabled={disabled} aria-describedby={`${id}-zone`}>
    <legend>{label}</legend>
    <div className="meeting-picker-row">
      <label htmlFor={`${id}-date`}>{label} date
        <input id={`${id}-date`} required type="date" min={preserved ? undefined : minimum.split('T')[0]} value={date} onChange={event => onChange(`${event.target.value}T${time}`)} />
      </label>
      <label htmlFor={`${id}-time`}>{label} time
        <input id={`${id}-time`} ref={timeRef} required type="time" step={preserved ? 60 : 900} value={time} onChange={event => onChange(`${date}T${event.target.value}`)} aria-invalid={past || undefined} />
      </label>
    </div>
    <p id={`${id}-zone`} className="muted schedule-zone">{timezone || 'Choose a timezone'} · 15-minute steps</p>
    {past && <p className="meeting-error">Choose a future meeting time.</p>}
  </fieldset>;
}
